import React from "react";
import { useNavigate } from "react-router-dom";
import type { User } from "@domain/Auth";
import { UserContext } from "@gui/contexts/user/UserContext";
import { ISSUE_COUNT_PATHS, useIssueCounts } from "@gui/contexts/issueCounts/IssueCountsContext";
import { navLinks, type NavLink } from "@gui/routes/navigation";

/** A Home card: its link plus the issue-count badge it shows, if any. */
export type HomeCard = NavLink & {
	/** The count is still loading: a spinner badge. */
	countLoading: boolean;
	/** A resolved count above zero; undefined shows no badge. */
	count: number | undefined;
};

/** Home: one card per page the user can open, grouped as the sidebar groups them (main, admin,
 *  developer), with the same issue-count badges. */
export function useHomeViewModel() {
	const navigate = useNavigate();
	const user = React.useContext(UserContext) as User | undefined;
	const issueCounts = useIssueCounts();

	// Same three states as the sidebar badge: in flight, a count above zero, or nothing (a zero
	// count, or no fetcher for this link).
	const toCard = (link: NavLink): HomeCard => {
		const hasFetcher = ISSUE_COUNT_PATHS.has(link.to);
		const count = issueCounts.counts[link.to];
		return {
			...link,
			countLoading: hasFetcher && count === undefined && issueCounts.loading,
			count: hasFetcher && count !== undefined && count > 0 ? count : undefined,
		};
	};

	// The Home link itself is skipped: it is this page.
	const sections = [
		navLinks("main", user).filter((link) => link.to !== "/home"),
		navLinks("admin", user),
		navLinks("developer", user),
	]
		.filter((links) => links.length > 0)
		.map((links) => links.map(toCard));

	return { sections, open: (to: string) => navigate(to) };
}
